import React, {forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState} from "react";
import useIsMounted from "../../../hooks/use-is-mounted";
import {createUUId, deepEqual} from "../../../../core/services/utils/utils";
import {Box, Button, CircularProgress, FormHelperText, Typography} from "@mui/material";
import {DataGridColumnAlignments, DataGridColumnPinnedTypes, DataGridColumnTypes, DataGridExoticColumnFields, useDataGridSavedState} from "../../../../packages/data-grid";
import {ProductDimensionManagementDataGridColumnNames} from "../../../../core/constants/table/data-grid-column-keys";
import IconButton from "../../base/button/icon_button";
import {DeleteOutlined, EditOutlined} from "@mui/icons-material";
import PartnerDataGrid from "../../../containers/data-grid";
import {setReduxDialogState} from "../../../redux/slices/dialogs/actions";
import ReduxStoreRoutes from "../../../../core/models/static/routes/redux-store-routes";
import {useAppDispatch} from "../../../redux";
import SaveOrRollback from "../save-or-rollback";
import Form, {MuiRFF, Yup} from '../../form';
import ValidateMessages from "../../../../core/models/static/texts/vallidate-messages";
import {DimensionMetricController, ProductDimensionUnit, useSelectedDimensionMetrics} from "./unit-management";
import Api from "../../../../core/services/api/api";
import VendorPartAutocomplete from "../../text-fields/autocomplete/vendor-autocomplete/vendor-part";
import useAccessLevels from "../../../hooks/use-access-levels";
import ToastService from "../../../../core/services/toast";
import AddVendorModal from "../../dialogs/add-vendor";

const dataGridSaveConfig = {
    name: "ManageProductDimensionsDataGrid",
    version: 2,
};

const rowIdentifier = (uniqueId) => `manage-product-dimensions-row-${uniqueId}`;

const initialAddVendorDialogState = {open: false, setOpen: () => void 0, initialValues: {}, addVendor: () => void 0}

const ManageProductDimensions = forwardRef(({
                                                title,
                                                partNo,
                                                partId,
                                                onSelect,
                                                selectedId,
                                            }, ref) => {
    const {lengthUnit, weightUnit, transformLengthValue, transformWeightValue} = useSelectedDimensionMetrics();
    const [loadingDimensions, setLoadingDimensions] = useState(true);
    const [dimensions, setDimensions] = useState([]);
    const [inEditModeIds, setInEditModeIds] = useState([]);
    const [submittingIds, setSubmittingIds] = useState([]);
    const [removingIds, setRemovingIds] = useState([]);
    const isMounted = useIsMounted();
    const operatorAccessLevels = useAccessLevels();
    const [addVendorDialogState, setAddVendorDialogState] = useState(initialAddVendorDialogState);

    const dispatch = useAppDispatch();

    /**
     * Expose imperative methods to parent components
     */
    useImperativeHandle(ref, () => ({
        getDimensions: () => dimensions,
        getLoadingState: () => loadingDimensions,
    }), [dimensions, loadingDimensions])

    const schema = useMemo(() => {
        let seenGeneralVendor = dimensions.some(dimension => !dimension.vendorCode);
        const entries = dimensions.map((dimension, i, dimensions) => {
            return [
                rowIdentifier(dimension.uniqueId),
                Yup.object().shape({
                    vendor: (seenGeneralVendor ? Yup.mixed().nullable() : Yup.mixed().nullable().required(ValidateMessages.required))
                        .test('unique-vendor-code', ValidateMessages.incorrectType('unique Vendor'), function (value) {
                            const allFound = dimensions.filter(d => (d.vendorCode?.trim() === value?.vendorCode?.trim() && !!value?.vendorCode?.trim()) || (!value?.vendorCode && !d.vendorCode));
                            return !allFound.length || dimensions.indexOf(dimension) === dimensions.indexOf(allFound[0]);
                        }),
                    height: Yup.number().moreThan(0, ValidateMessages.greaterThan('0')).required(ValidateMessages.required).typeError(ValidateMessages.incorrectType('number')).nullable(),
                    width: Yup.number().moreThan(0, ValidateMessages.greaterThan('0')).required(ValidateMessages.required).typeError(ValidateMessages.incorrectType('number')).nullable(),
                    length: Yup.number().moreThan(0, ValidateMessages.greaterThan('0')).required(ValidateMessages.required).typeError(ValidateMessages.incorrectType('number')).nullable(),
                    weight: Yup.number().moreThan(0, ValidateMessages.greaterThan('0')).required(ValidateMessages.required).typeError(ValidateMessages.incorrectType('number')).nullable(),
                    comment: Yup.string().nullable().trim(),
                })
            ];
        });
        return Yup.object().shape(Object.fromEntries(entries));
    }, [dimensions]);

    const validate = useMemo(() => MuiRFF.makeValidate(schema), [schema]);
    const required = useMemo(() => MuiRFF.makeRequired(schema), [schema]);

    const initialValues = useMemo(() => {
        const entries = dimensions.map(dimension => ([
            rowIdentifier(dimension.uniqueId), {
                vendor: {
                    vendorId: dimension.vendorId ?? null,
                    vendorCode: dimension.vendorCode?.toString() ?? "",
                },
                height: dimension.height ? transformLengthValue(dimension.height, {fromDefault: true, displayDecimals: 5}) : '',
                width: dimension.width ? transformLengthValue(dimension.width, {fromDefault: true, displayDecimals: 5}) : '',
                length: dimension.length ? transformLengthValue(dimension.length, {fromDefault: true, displayDecimals: 5}) : '',
                weight: dimension.weight ? transformWeightValue(dimension.weight, {fromDefault: true, displayDecimals: 5}) : '',
                comment: dimension.comment ?? "",
            }
        ]))
        return Object.fromEntries(entries);
    }, [dimensions, transformLengthValue, transformWeightValue]);

    /**
     * Fetch product dimensions from API
     * @type {(function(): void)}
     */
    const getProductDimensions = useCallback(async () => {
        setLoadingDimensions(true);
        const response = await Api.getPartSizesList(partId);
        await new Promise((resolve) => setTimeout(resolve, 1000));
        if (!isMounted())
            return;
        setLoadingDimensions(false);
        if (response.resultFlag) {
            const items = response.data?.map(e => ({
                ...e,
                uniqueId: createUUId(true),
            })) ?? []
            setDimensions(items);
        }
    }, [isMounted, partId])


    /**
     * Calls the getProductDimensions function to load dimensions on component mount
     */
    useEffect(() => {
        if (!partId)
            return;
        getProductDimensions();
    }, [getProductDimensions, partId]);

    /**
     * Toggle edit mode for a specific dimension entry
     * @type {(function(*): void)|*}
     */
    const toggleEditMode = useCallback(async (uniqueId, forcedValue) => {
        await new Promise((resolve) => {
            setInEditModeIds((prev) => {
                const value = forcedValue ?? !prev.includes(uniqueId);
                resolve(value);
                if (value) {
                    return [...prev, uniqueId];
                } else {
                    return prev.filter((id) => id !== uniqueId);
                }
            });
        });
    }, [])

    /**
     * Add a new dimension row for input
     * @type {(function(): void)|*}
     */
    const addNewDimensionRow = useCallback(() => {
        const newDimension = {
            uniqueId: createUUId(true),
        };
        setDimensions((prev) => [...prev, newDimension]);
        toggleEditMode(newDimension.uniqueId, true);
    }, [toggleEditMode]);

    /**
     * Fetches the vendor information by the vendor code provided in the vendor info, and if a vendor is found, returns it. If no vendor is found and the user has permission to create a vendor,
     * it opens the add vendor dialog with the initial values set to the vendor info provided, and returns a promise that resolves with the new vendor created once the add vendor dialog is submitted,
     * or null if the added vendor dialog is closed without creating a new vendor.
     * @type {(function(*): Promise<*>)|*}
     */
    const getOrCreateVendor = useCallback(async (vendorInfo) => {
        const response = await Api.getVendorInformation(vendorInfo.code);
        if (!isMounted())
            return;
        if (!response?.resultFlag)
            return;
        // found, so we return
        if (response?.data)
            return response?.data;
        if (!operatorAccessLevels.packages.base.createNewVendor) {
            ToastService.warning({
                title: "No Permission",
                message: "The selected vendor needs to have an online profile in our system yet you do not have permission to create them. Please contact your administrator."
            })
            return;
        }
        // not found, so we need to create it and we can create the vendor.
        return await new Promise((resolve) => {
            ToastService.warning({
                title: "Create Vendor Profile",
                message: "The selected vendor needs to have an online profile in our system."
            })
            setAddVendorDialogState({
                open: true,
                initialValues: vendorInfo,
                setOpen: () => {
                    resolve(null);
                    setAddVendorDialogState(initialAddVendorDialogState);
                },
                addVendor: async (forApi, onResponse) => {
                    const response = await Api.createVendorOnlineProfile(forApi);
                    if (response?.resultFlag) {
                        resolve(response?.data);
                    }
                    onResponse(response);
                },
            })
        });
    }, [isMounted, operatorAccessLevels.packages.base.createNewVendor])


    /**
     * Create a new product dimension
     * @type {(function(*): Promise<void>)|*}
     */
    const createProductDimension = useCallback(async (dimension, values) => {
        setSubmittingIds((prev) => [...prev, dimension.uniqueId]);
        let vendorId = values.vendor?.vendorId;
        if (!vendorId) {
            const vendorInfo = values.vendor?.vendorCode?.trim() ? await getOrCreateVendor({code: values.vendor?.vendorCode?.trim() ?? ""}) : null;
            if (!isMounted())
                return;
            vendorId = vendorInfo?.id;
        }
        const forApi = {
            partId: partId,
            vendorId: vendorId || null,
            height: Number(values.height),
            width: Number(values.width),
            length: Number(values.length),
            weight: Number(values.weight),
            comment: values.comment,
        }
        const response = await Api.createPartSize(forApi);
        if (!isMounted())
            return;
        setSubmittingIds((prev) => prev.filter((id) => id !== dimension.uniqueId));
        if (response.resultFlag) {
            const newDimension = {
                ...dimension,
                ...(response.data ?? {}),
                comment: values.comment,
            };
            setDimensions((prev) => prev.map((dim) => dim.uniqueId === newDimension.uniqueId ? newDimension : dim));
            toggleEditMode(newDimension.uniqueId, false);
        }
    }, [getOrCreateVendor, isMounted, partId, toggleEditMode]);

    /**
     * Update an existing product dimension
     * @type {(function(*): Promise<void>)|*}
     */
    const updateProductDimension = useCallback(async (dimension, values) => {
        setSubmittingIds((prev) => [...prev, dimension.uniqueId]);
        let vendorId = values.vendor?.vendorId;
        if (!vendorId) {
            const vendorInfo = values.vendor?.vendorCode?.trim() ? await getOrCreateVendor({code: values.vendor?.vendorCode?.trim() ?? ""}) : null;
            if (!isMounted())
                return;
            vendorId = vendorInfo?.id;
        }
        const forApi = {
            id: dimension.id,
            partId: partId,
            vendorId: vendorId || null,
            height: Number(values.height),
            width: Number(values.width),
            length: Number(values.length),
            weight: Number(values.weight),
            comment: values.comment,
        }
        const response = await Api.updatePartSize(forApi);
        if (!isMounted())
            return;
        setSubmittingIds((prev) => prev.filter((id) => id !== dimension.uniqueId));
        if (response.resultFlag) {
            const newDimension = {
                ...dimension,
                vendorCode: !values.vendor?.vendorCode?.trim() ? null : values.vendor.vendorCode.trim(),
                height: Number(values.height),
                width: Number(values.width),
                length: Number(values.length),
                weight: Number(values.weight),
                comment: values.comment,
            };
            toggleEditMode(newDimension.uniqueId, false);
            setDimensions((prev) => prev.map((dim) => dim.uniqueId === newDimension.uniqueId ? newDimension : dim));
            // After updating, if the updated dimension is selected, call onSelect to update a parent component
            if (selectedId === newDimension.uniqueId) {
                if (onSelect) {
                    onSelect(newDimension);
                }
            }
        }
    }, [getOrCreateVendor, partId, isMounted, toggleEditMode, selectedId, onSelect]);

    /**
     * Opens the confirmation dialog for removing a dimension
     * @type {function(*): Promise<unknown>}
     */
    const getRemoveConfirmation = useCallback((dimension) => {
        return new Promise((resolve) => {
            dispatch(setReduxDialogState(ReduxStoreRoutes.dialogs.removeConfirmation, {
                open: true,
                data: null,
                removedEntryTitle: dimension.vendorCode ? `the dimension for ${dimension.vendorCode}` : 'the default dimension',
                onClose: async (confirmed) => {
                    resolve(confirmed);
                    return true;
                }
            }));
        });
    }, [dispatch]);

    /**
     * Delete a product dimension
     * @type {(function(*): Promise<void>)|*}
     */
    const deleteProductDimension = useCallback(async (dimension, skipConfirmation = false) => {
        const hasId = Boolean(dimension.id);
        const hasValues = Boolean(dimension.height || dimension.width || dimension.length || dimension.weight || dimension.comment);
        if (!skipConfirmation && (hasId || hasValues)) {
            // We need to ask for user confirmation
            const confirmed = await getRemoveConfirmation(dimension);
            if (!confirmed) {
                return;
            }
        }
        if (!hasId) {
            setDimensions((prev) => prev.filter((dim) => dim.uniqueId !== dimension.uniqueId));
            return;
        }
        setRemovingIds((prev) => [...prev, dimension.uniqueId]);
        const response = await Api.deletePartSize({id: dimension.id,});
        if (!isMounted())
            return;
        setRemovingIds((prev) => prev.filter((id) => id !== dimension.uniqueId));
        if (response.resultFlag) {
            setDimensions((prev) => prev.filter((dim) => dim.uniqueId !== dimension.uniqueId));
            setInEditModeIds((prev) => prev.filter((id) => id !== dimension.uniqueId));
            if (selectedId === dimension.uniqueId) {
                if (onSelect) {
                    onSelect(null);
                }
            }
        }
    }, [getRemoveConfirmation, isMounted, onSelect, selectedId]);

    /**
     * Memo version of column in the data grid
     * @type {DataGridColumn[]}
     */
    const columns = useMemo(() =>
        /** @type {DataGridColumn[]}*/
        [
            {
                name: DataGridExoticColumnFields.selection,
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.element,
                resizable: false,
                visibilityToggleable: false,
                pinnedToggleable: false,
                reorderable: false,
                sortable: false,
                pinned: true,
            },
            {
                title: "Vendor Code",
                name: ProductDimensionManagementDataGridColumnNames.vendorCode,
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.string,
                sortable: false,
                pinned: true,
                width: 150,
            },
            {
                title: "Length",
                name: ProductDimensionManagementDataGridColumnNames.length,
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.element,
                sortable: false,
                width: 80,
            },
            {
                title: "Width",
                name: ProductDimensionManagementDataGridColumnNames.width,
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.element,
                sortable: false,
                width: 80,
            },
            {
                title: "Height",
                name: ProductDimensionManagementDataGridColumnNames.height,
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.element,
                sortable: false,
                width: 80,
            },
            {
                title: "Weight",
                name: ProductDimensionManagementDataGridColumnNames.weight,
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.string,
                sortable: false,
                width: 80,
            },
            // {
            //     title: "Comment",
            //     name: ProductDimensionManagementDataGridColumnNames.comment,
            //     alignment: DataGridColumnAlignments.left,
            //     type: DataGridColumnTypes.string,
            //     sortable: false,
            //     width: 250,
            // },
            {
                name: ProductDimensionManagementDataGridColumnNames.actions,
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.element,
                sortable: false,
                width: 50,
                pinned: true,
                pinnedType: DataGridColumnPinnedTypes.right,
            }
        ], []);


    const [dataGridColumns, , , dataGridDensity] = useDataGridSavedState({
        columns: columns,
        name: dataGridSaveConfig.name,
        version: dataGridSaveConfig.version,
    });

    /**
     * Data grid rows entities
     * @returns {DataGridRow[]}
     */
    const dataGridRows = useCallback((formProps) => {
        const {form, errors} = formProps;
        return [
            ...dimensions.map(dimension => {
                const inEditMode = inEditModeIds.includes(dimension.uniqueId);
                const loading = submittingIds.includes(dimension.uniqueId);
                const removing = removingIds.includes(dimension.uniqueId);
                return ({
                    key: dimension.uniqueId,
                    data: dimension,
                    cells: {
                        [DataGridExoticColumnFields.selection]: inEditMode || (!onSelect && !selectedId) ? " " : undefined,
                        ...(!inEditMode && {
                            [ProductDimensionManagementDataGridColumnNames.vendorCode]:
                                <Box>
                                    <Typography variant={'body2'} fontWeight={600} sx={{display: 'flex', gap: 0.5, alignItems: 'center'}} component='div'>
                                        {dimension.vendorCode || "General"}
                                    </Typography>
                                    <VendorPartAutocomplete
                                        form
                                        partId={partId}
                                        partNo={partNo}
                                        name={rowIdentifier(dimension.uniqueId) + '.vendor'}
                                        required={required?.[rowIdentifier(dimension.uniqueId)]?.vendor}
                                        fullWidth
                                        sx={{display: 'none'}}
                                    />
                                    {
                                        errors?.[rowIdentifier(dimension.uniqueId)]?.vendor &&
                                        <FormHelperText error variant={'outlined'}>
                                            {errors?.[rowIdentifier(dimension.uniqueId)]?.vendor}
                                        </FormHelperText>
                                    }
                                </Box>,
                            [ProductDimensionManagementDataGridColumnNames.height]:
                                <Box>
                                    <Typography variant={'body2'} fontWeight={600} sx={{display: 'flex', gap: 0.5, alignItems: 'center'}} component='div'>
                                        {
                                            !!dimension.height
                                                ? <>{transformLengthValue(dimension.height, {fromDefault: true})}<ProductDimensionUnit unit={lengthUnit} backgroundColor={"transparent"}/></>
                                                : ' '
                                        }
                                    </Typography>
                                    <MuiRFF.TextField
                                        name={rowIdentifier(dimension.uniqueId) + '.height'}
                                        required={required?.[rowIdentifier(dimension.uniqueId)]?.height}
                                        type={"number"}
                                        InputProps={{sx: {display: 'none'}}}
                                    />
                                </Box>,
                            [ProductDimensionManagementDataGridColumnNames.width]:
                                <Box>
                                    <Typography variant={'body2'} fontWeight={600} sx={{display: 'flex', gap: 0.5, alignItems: 'center'}} component='div'>
                                        {
                                            !!dimension.width
                                                ? <>{transformLengthValue(dimension.width, {fromDefault: true})} <ProductDimensionUnit unit={lengthUnit} backgroundColor={"transparent"}/></>
                                                : ' '
                                        }
                                    </Typography>
                                    <MuiRFF.TextField
                                        name={rowIdentifier(dimension.uniqueId) + '.width'}
                                        required={required?.[rowIdentifier(dimension.uniqueId)]?.width}
                                        type={"number"}
                                        InputProps={{sx: {display: 'none'}}}
                                    />
                                </Box>,
                            [ProductDimensionManagementDataGridColumnNames.length]:
                                <Box>
                                    <Typography variant={'body2'} fontWeight={600} sx={{display: 'flex', gap: 0.5, alignItems: 'center'}} component='div'>
                                        {
                                            !!dimension.length
                                                ? <>{transformLengthValue(dimension.length, {fromDefault: true})} <ProductDimensionUnit unit={lengthUnit} backgroundColor={"transparent"}/></>
                                                : ' '
                                        }
                                    </Typography>
                                    <MuiRFF.TextField
                                        name={rowIdentifier(dimension.uniqueId) + '.length'}
                                        required={required?.[rowIdentifier(dimension.uniqueId)]?.length}
                                        type={"number"}
                                        InputProps={{sx: {display: 'none'}}}
                                    />
                                </Box>,
                            [ProductDimensionManagementDataGridColumnNames.weight]:
                                <Box>
                                    <Typography variant={'body2'} fontWeight={600} sx={{display: 'flex', gap: 0.5, alignItems: 'center'}} component='div'>
                                        {
                                            !!dimension.weight
                                                ? <>{transformWeightValue(dimension.weight, {fromDefault: true})} <ProductDimensionUnit unit={weightUnit} backgroundColor={"transparent"}/></>
                                                : ' '
                                        }
                                    </Typography>
                                    <MuiRFF.TextField
                                        name={rowIdentifier(dimension.uniqueId) + '.weight'}
                                        required={required?.[rowIdentifier(dimension.uniqueId)]?.weight}
                                        type={"number"}
                                        InputProps={{sx: {display: 'none'}}}
                                    />
                                </Box>,
                            [ProductDimensionManagementDataGridColumnNames.comment]: dimension.comment,
                            [ProductDimensionManagementDataGridColumnNames.actions]:
                                <Box sx={{display: 'flex', gap: 0.5}}>
                                    <IconButton
                                        onPressed={() => toggleEditMode(dimension.uniqueId, true)}
                                        color={'paper.secondary'}
                                        disabled={loading || removing}
                                    >
                                        {
                                            loading
                                                ? <CircularProgress size={20}/>
                                                : <EditOutlined/>
                                        }
                                    </IconButton>
                                    <IconButton onPressed={() => deleteProductDimension(dimension)} color={'paper.error'} disabled={loading || removing}>
                                        {
                                            removing
                                                ? <CircularProgress size={20}/>
                                                : <DeleteOutlined/>
                                        }
                                    </IconButton>
                                </Box>
                        }),
                        ...(inEditMode && {
                            [ProductDimensionManagementDataGridColumnNames.vendorCode]:
                                <VendorPartAutocomplete
                                    form
                                    partId={partId}
                                    partNo={partNo}
                                    name={rowIdentifier(dimension.uniqueId) + '.vendor'}
                                    required={required?.[rowIdentifier(dimension.uniqueId)]?.vendor}
                                    fullWidth
                                    label={"Vendor"}
                                    textFieldProps={{size: 'small',}}
                                    disabled={loading}
                                />,
                            [ProductDimensionManagementDataGridColumnNames.height]:
                                <MuiRFF.TextField
                                    name={rowIdentifier(dimension.uniqueId) + '.height'}
                                    required={required?.[rowIdentifier(dimension.uniqueId)]?.height}
                                    size={"small"}
                                    type={"number"}
                                    disabled={loading}
                                    InputProps={{
                                        endAdornment: <ProductDimensionUnit unit={lengthUnit} inInput/>,
                                    }}
                                />,
                            [ProductDimensionManagementDataGridColumnNames.width]:
                                <MuiRFF.TextField
                                    name={rowIdentifier(dimension.uniqueId) + '.width'}
                                    required={required?.[rowIdentifier(dimension.uniqueId)]?.width}
                                    size={"small"}
                                    type={"number"}
                                    disabled={loading}
                                    InputProps={{
                                        endAdornment: <ProductDimensionUnit unit={lengthUnit} inInput/>,
                                    }}
                                />,
                            [ProductDimensionManagementDataGridColumnNames.length]:
                                <MuiRFF.TextField
                                    name={rowIdentifier(dimension.uniqueId) + '.length'}
                                    required={required?.[rowIdentifier(dimension.uniqueId)]?.length}
                                    size={"small"}
                                    type={"number"}
                                    disabled={loading}
                                    InputProps={{
                                        endAdornment: <ProductDimensionUnit unit={lengthUnit} inInput/>,
                                    }}

                                />,
                            [ProductDimensionManagementDataGridColumnNames.weight]:
                                <MuiRFF.TextField
                                    name={rowIdentifier(dimension.uniqueId) + '.weight'}
                                    required={required?.[rowIdentifier(dimension.uniqueId)]?.weight}
                                    size={"small"}
                                    type={"number"}
                                    disabled={loading}
                                    InputProps={{
                                        endAdornment: <ProductDimensionUnit unit={weightUnit} inInput/>,
                                    }}
                                />,
                            [ProductDimensionManagementDataGridColumnNames.comment]:
                                <MuiRFF.TextField
                                    name={rowIdentifier(dimension.uniqueId) + '.comment'}
                                    required={required?.[rowIdentifier(dimension.uniqueId)]?.comment}
                                    fullWidth
                                    size={"small"}
                                    disabled={loading}
                                />,
                            [ProductDimensionManagementDataGridColumnNames.actions]:
                                <SaveOrRollback
                                    loading={loading}
                                    column={false}
                                    showText={false}
                                    show
                                    onRollback={() => {
                                        if (!dimension.id) {
                                            //  remove the row from the grid
                                            void deleteProductDimension(dimension, true)
                                        } else {
                                            toggleEditMode(dimension.uniqueId, false)
                                        }
                                    }}
                                    onSave={async () => {
                                        form.change('flag', dimension.uniqueId);
                                        // we intentionally mark all as valid so we can then submit the form and show errors if any.
                                        form.getRegisteredFields().forEach((name) => {
                                            form.resetFieldState(name);
                                        })
                                        void form.submit();
                                    }}
                                />,
                        }),
                    },
                })
            }),
            ...(!loadingDimensions ? [
                {
                    key: 'add-new-dimension-row',
                    data: null,
                    cells: {
                        [DataGridExoticColumnFields.selection]: " ",
                        [ProductDimensionManagementDataGridColumnNames.vendorCode]:
                            <Box sx={{display: 'flex', justifyContent: 'center', width: '100%'}}>
                                <Button onClick={addNewDimensionRow} variant={"outlined"} size={"small"}>
                                    New Dimension
                                </Button>
                            </Box>,
                        [ProductDimensionManagementDataGridColumnNames.height]: null,
                        [ProductDimensionManagementDataGridColumnNames.width]: null,
                        [ProductDimensionManagementDataGridColumnNames.length]: null,
                        [ProductDimensionManagementDataGridColumnNames.weight]: null,
                        [ProductDimensionManagementDataGridColumnNames.comment]: null,
                        [ProductDimensionManagementDataGridColumnNames.actions]: null,
                    },
                }
            ] : []),
        ]
    }, [dimensions, loadingDimensions, addNewDimensionRow, inEditModeIds, submittingIds, removingIds, onSelect, selectedId, partId, partNo, required, transformLengthValue, lengthUnit, transformWeightValue, weightUnit, toggleEditMode, deleteProductDimension]);

    /**
     * Sets the [selectedOrders] state value with each change of the row selection state of the data-grid.
     * @param _
     * @param {DataGridRowSelectionInfo} current
     */
    const onRowSelectionInfoChanged = useCallback(async (_, {allRowsSelected, selectedRows}) => {
        const selectedDimension = allRowsSelected ? dimensions[0] : selectedRows[0]?.data;
        if (onSelect) {
            setTimeout(() => {
                if (!isMounted())
                    return;
                onSelect(selectedDimension);
            }, 200)
        }
    }, [dimensions, isMounted, onSelect])

    /**
     * Handles form submission for updating or creating product dimensions
     * @type {(function(*, *): Promise<undefined|{}>)|*}
     */
    const submitForm = useCallback(async (values, form) => {
        const flag = form.getState().values.flag;
        try {
            if (!flag) {
                return;
            }
            const dimension = dimensions.find(d => d.uniqueId === flag);
            const value = values[rowIdentifier(flag)]
            const initialValue = initialValues[rowIdentifier(flag)]
            const hasChanged = !deepEqual(value, initialValue);
            if (!hasChanged && !!dimension.id) {
                // No changes were made
                toggleEditMode(dimension.uniqueId, false);
            } else {
                // We have to keep these in assume. Since we reset before submitting, we have to get the errors after submit.
                const errors = form.getState().errors[rowIdentifier(flag)];
                if (errors) {
                    return {
                        [rowIdentifier(flag)]: errors,
                    };
                }
                const normalizedValue = {
                    vendor: value.vendor,
                    comment: value.comment,
                    height: Number(transformLengthValue(Number(value.height), {toDefault: true, displayDecimals: 2})),
                    width: Number(transformLengthValue(Number(value.width), {toDefault: true, displayDecimals: 2})),
                    length: Number(transformLengthValue(Number(value.length), {toDefault: true, displayDecimals: 2})),
                    weight: Number(transformWeightValue(Number(value.weight), {toDefault: true, displayDecimals: 3})),
                }
                // if we have errors, we need to show it.
                if (dimension.id) {
                    void updateProductDimension(dimension, normalizedValue)
                } else {
                    void createProductDimension(dimension, normalizedValue)
                }
            }
        } finally {
            form.change('flag', null);
        }
    }, [createProductDimension, dimensions, initialValues, toggleEditMode, transformLengthValue, transformWeightValue, updateProductDimension])

    return (
        <>
            <Box sx={{display: 'flex', flexDirection: 'column', gap: 2, width: '100%'}}>
                <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, width: '100%'}}>
                    {
                        !!title &&
                        <Typography variant="subtitle1" fontWeight={600}>
                            {title}
                        </Typography>
                    }
                    {
                        !title &&
                        <div/>
                    }
                    <DimensionMetricController/>
                </Box>
                <Form validate={validate} initialValues={initialValues} onSubmit={submitForm}>
                    {(formProps) => (
                        <PartnerDataGrid
                            {...dataGridSaveConfig}
                            initialLayoutState={{columns: columns,}}
                            columns={dataGridColumns}
                            rows={dataGridRows(formProps)}
                            singleSelectionOnly
                            hideExporting
                            hidePagination
                            hidePageSize
                            showSelectionOnHover={false}
                            onRowSelectionInfoChanged={onRowSelectionInfoChanged}
                            state={{
                                pagination: {pageSize: dimensions.length, total: dimensions.length, currentPage: 1},
                                loading: {state: loadingDimensions},
                                density: dataGridDensity,
                            }}
                        />
                    )}
                </Form>
            </Box>
            <AddVendorModal
                addVendor={addVendorDialogState.addVendor}
                initialValues={addVendorDialogState.initialValues}
                open={addVendorDialogState.open}
                setOpen={addVendorDialogState.setOpen}
            />
        </>
    )
})


export default ManageProductDimensions;